import Api from "./../config/api";
import { IDestinationFilters } from "../models/ExemptDestinations/ExemptDestination";

interface IAxiosResponse {
    data: any
}

/**
 * /callcenter/admin/exempt-destinations — same middleware and permission stack as the
 * whitelist and robocall screens.
 *
 * Every mutating call carries a comment, and the API rejects one without it. An exemption
 * switches off the whole inbound spam stack for a number; "why is this DID not being
 * filtered?" is asked months later, by somebody else.
 */
export class ExemptDestinationsActions {

    private static query(filters: IDestinationFilters, extra: string[] = []): string {
        const query: string[] = [...extra]
        if (filters.q && filters.q !== '') { query.push(`q=${encodeURIComponent(filters.q)}`) }
        if (filters.include_removed) { query.push('include_removed=1') }
        return query.length > 0 ? `?${query.join('&')}` : ''
    }

    static getDestinations(filters: IDestinationFilters, page?: number, perPage?: number): Promise<any> {
        const extra: string[] = []
        if (page && page > 1) { extra.push(`page=${page}`) }
        if (perPage) { extra.push(`per_page=${perPage}`) }
        return Api.get("/exempt-destinations" + this.query(filters, extra))
            .then((res: IAxiosResponse) => res.data)
    }

    /** One number: current state, full history, and whether it is live in the cache. */
    static getDestination(phone: string): Promise<any> {
        return Api.get("/exempt-destinations/" + encodeURIComponent(phone))
            .then((res: IAxiosResponse) => res.data)
    }

    /**
     * Exempt a number. Any US/Canada format is accepted — the API normalises before storing,
     * so "(805) 892-6988" and "+18058926988" are the same number to it.
     *
     * Re-adding a number that was removed earlier restores the existing row and keeps its
     * history, which is why this can answer 200 as well as 201.
     */
    static createDestination(phone: string, comment: string): Promise<any> {
        return Api.post("/exempt-destinations", { phone, comment })
            .then((res: IAxiosResponse) => res.data)
    }

    /** Edit the reason, or bring a removed number back. The number itself is not editable. */
    static updateDestination(id: number, comment: string): Promise<any> {
        return Api.patch(`/exempt-destinations/${id}`, { comment })
            .then((res: IAxiosResponse) => res.data)
    }

    /** Stop exempting a number: it goes back under every inbound spam filter. */
    static deleteDestination(id: number, comment: string): Promise<any> {
        return Api.delete(`/exempt-destinations/${id}`, { data: { comment } })
            .then((res: IAxiosResponse) => res.data)
    }
}
